/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
    createContext, useContext, ReactNode,
    useMemo,
    useEffect,
    useState,
    useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useThreeBProductType } from 'hooks/isThreeBProduct';
import { useContactGroupOwners } from 'hooks/helpers/useContactGroupOwners';
import { useManagerProfile } from 'prodivers/managerProfile';
import { accessPolicies } from 'prodivers/managerProfile/constants';
import { useSchema } from 'domain/ClientService';

import { getClientId } from 'utils';
import ServiceManager from 'services/ServiceManager';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import useQueryParams from 'hooks/helpers/useQueryParams';
import { CaseOrderStatus } from 'components/DocBox/DocBox.types';
import { THREEBTYPES } from 'constants/constants';
import { CreateCaseContextProps, CreateCaseState } from './CreateCaseManager.types';
import { actionTypes, CreateCaseManagerReducer } from './CreateCaseManagerReducer';
import useGetActive3bGroup from './hooks/useGetActive3bPortfolios';
import { STATIC_CITY_FOR_BENEFICIARY, STATIC_ZIP_CODE_FOR_BENEFICIARY } from '../constants';

export const createNewCaseStorageKey = 'createNewCase';
export const createCasePoAStorageKey = 'create-case--PoA';

const defaultState: CreateCaseState = {
    typeOfOrderData: {},
    reasonForSaleData: {},
    bankInformationData: {},
    saveCaseManagementData: () => {},
};

const CreateCaseManagerContext = createContext<CreateCaseContextProps | undefined>(undefined);

export const CreateCaseManagerProvider = ({ children }: { children: ReactNode }) => {
    const getInitialState = (): CreateCaseState => {
        const stored = sessionStorage.getItem(createNewCaseStorageKey);

        if (stored) {
            try {
                const parsed = JSON.parse(stored);

                return { ...defaultState, ...parsed };
            } catch {
                return defaultState;
            }
        }

        return defaultState;
    };

    const [state, dispatch] = React.useReducer(CreateCaseManagerReducer, getInitialState());

    const { t, i18n: { language } } = useTranslation();
    const { hasAccessPolicy } = useManagerProfile();
    const { dfsClientId, portfolioId } = useParams();
    const clientId = getClientId(dfsClientId);

    const { getParam } = useQueryParams();

    const investmentApplicationId = (getParam('investmentApplicationId') as string);

    const {
        data: portfolioDetailsData,
    } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getPortfolioDetails',
        payload: [portfolioId, clientId, { language }],
        enabled: !!portfolioId && !!clientId,
    });

    const portfolio = portfolioDetailsData?.data ?? null;

    const { mainOwner, jaClient } = useContactGroupOwners({ connectionGroupId: portfolio?.ContactGroupId });

    const getPortfolioDetails = useCallback(async ({ _portfolioId }) => {
        const params = { language };
        const response = await ServiceManager.portfolioManagement('getPortfolioDetails', [_portfolioId, clientId, params]);

        return response;
    }, [clientId, language]);

    const isPA = useMemo(() => mainOwner?.age && (+mainOwner.age < 18), [mainOwner?.age]);
    const isKuDi = useMemo(() => hasAccessPolicy(accessPolicies?.EnableBeneficiaryChange), [hasAccessPolicy]);

    const {
        isAzp, isPk,
    } = useThreeBProductType(
        { product: { externalId: portfolio?.Product?.ExternalId } },
    );

    const poaContactId = sessionStorage.getItem(createCasePoAStorageKey);

    const { dataLists } = useSchema();

    const {
        data: casesData,
    } = useGetCustomService({
        service: 'customCaseManagementServices', api: 'getCasesByPortfolioId', payload: { portfolioId }, enabled: !!portfolioId,
    });

    const hasPendingCase = useMemo(() => casesData?.data?.cases?.some(cse => cse?.orderType === 1 && cse?.orderStatus === CaseOrderStatus.ReadyToSubmit), [casesData?.data?.cases]);

    const {
        isLoading: isActive3bPortfoliosLoading, data: active3bPortfolios,
    } = useGetActive3bGroup({
        clientId, currentPortfolioId: portfolioId, currentContactGroupId: portfolio?.ContactGroupId, isJA: state?.isJA,
    });

    const {
        data: beneficiariesData,
    } = useGetCustomService({
        service: 'customCaseManagementServices', api: 'getBeneficiaries', payload: { portfolioId }, enabled: !!portfolioId,
    });

    const {
        data: investmentApplicationData,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getInvestmentApplication',
        payload: { investmentApplicationId },
        enabled: !!investmentApplicationId,
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 35,
    });
    const {
        data: lastApplicationByPortfolio,
    } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getLastApplicationByPortfolio',
        payload: { portfolioId, language },
        enabled: !!portfolioId && !investmentApplicationId,
        staleTime: 1000 * 60 * 30,
        gcTime: 1000 * 60 * 35,
    });

    useEffect(() => {
        if (investmentApplicationData?.data?.investmentDescription) {
            dispatch({
                type: actionTypes.SAVE_DATA,
                payload: {
                    investmentApplicationId,
                    isJA: investmentApplicationData?.data?.investmentDescription?.isJointAccount,
                    containerId: investmentApplicationData?.data?.investmentDescription?.containerId,
                },
            });
        } else if (lastApplicationByPortfolio?.data) {
            dispatch({
                type: actionTypes.SAVE_DATA,
                payload: {
                    investmentApplicationId: lastApplicationByPortfolio?.data?.investmentApplicationId,
                    isJA: lastApplicationByPortfolio?.data?.isJointAccount,
                },
            });
        }
    }, [investmentApplicationData?.data?.investmentDescription, lastApplicationByPortfolio?.data]);

    const beneficiaryInformationFromAPI = beneficiariesData?.data;

    const beneficiaryInfoStatic = {
        firstName: t('BeneficiaryInfo.firstName'),
        lastName: t('BeneficiaryInfo.lastName'),
        address: '',
        zipCodeOfBeneficiary: STATIC_ZIP_CODE_FOR_BENEFICIARY,
        cityOfBeneficiary: STATIC_CITY_FOR_BENEFICIARY,
    };

    // @ts-ignore
    const reasonForSaleOptions = useMemo(() => dataLists?.find(item => item.listKey === 'reasonForSale')?.listItems?.map(itm => (
        { value: `${itm?.id}`, label: itm?.label }
    )), [dataLists]) || [];

    // @ts-ignore
    const bankInformationOptions = useMemo(() => dataLists?.find(item => item.listKey === 'bankInformation')?.listItems?.map(itm => (
        { value: `${itm?.id}`, label: itm?.label }
    )), [dataLists]) || [];

    const isPaymentOrderPrivateAccount = useMemo(() => isPk && !state?.typeOfOrderData?.NeedCloseAccount, [isPk, state?.typeOfOrderData?.NeedCloseAccount]);

    const saveCaseManagementData = (value: Partial<CreateCaseState>) => {
        dispatch({ type: actionTypes.SAVE_DATA, payload: value });
    };

    const getPortfolioName = useCallback(async (_portfolioId) => {
        const portfolioDetails: any = await getPortfolioDetails({ _portfolioId });
        const portfolioProduct = portfolioDetails?.data?.Product;

        return portfolioProduct?.ExternalId === THREEBTYPES.pk ? '' : portfolioProduct?.Description || '';
    }, [getPortfolioDetails]);

    const [active3bPortfolioOptions, setActive3bPortfolioOptions] = useState<any[]>([]);

    useEffect(() => {
        const loadOptions = async () => {
            if (!active3bPortfolios) return;

            const options = await Promise.all(
                active3bPortfolios.map(async (_portfolio) => {
                    const name = await getPortfolioName(_portfolio?.portfolioId);

                    return {
                        value: _portfolio?.portfolioId.toString(),
                        label: `${name ? `${name} - ` : ''}${_portfolio?.modelPortfolioName || ''} (${_portfolio?.portfolioName || ''})`,
                    };
                }),
            );

            setActive3bPortfolioOptions(options);
        };

        loadOptions();
    }, [active3bPortfolios, getPortfolioName]);

    const cachedState = useMemo(() => ({
        ...state,
        isKuDi,
        active3bPortfolios,
        hasAdditional3b: active3bPortfolios?.length,
        isActive3bPortfoliosLoading,
        isJA: state?.isJA,
        isPOA: !!poaContactId,
        isPA,
        isPK: isPk,
        isAzp,
        currentPortfolioSelector: portfolio,
        mainOwner,
        jaClient,
        beneficiaryInfoStatic,
        reasonForSaleOptions,
        bankInformationOptions,
        beneficiaryInformationFromAPI,
        saveCaseManagementData,
        hasPendingCase,
        isPaymentOrderPrivateAccount,
        active3bPortfolioOptions,
    }), [
        state,
        isKuDi,
        active3bPortfolios,
        active3bPortfolios?.length,
        isActive3bPortfoliosLoading,
        state?.isJA,
        poaContactId,
        isPA,
        isPk,
        isAzp,
        portfolio,
        mainOwner,
        jaClient,
        beneficiaryInfoStatic,
        reasonForSaleOptions,
        bankInformationOptions,
        beneficiaryInformationFromAPI,
        saveCaseManagementData,
        hasPendingCase,
        isPaymentOrderPrivateAccount,
        active3bPortfolioOptions,
    ]);

    return (
        <CreateCaseManagerContext.Provider value={cachedState}>
            {children}
        </CreateCaseManagerContext.Provider>
    );
};

export const useCreateCaseManager = () => {
    const context = useContext(CreateCaseManagerContext);
    const t = useTranslation();

    React.useEffect(() => {
        if (context) {
            sessionStorage.setItem(createNewCaseStorageKey, JSON.stringify({
                typeOfOrderData: context.typeOfOrderData,
                reasonForSaleData: context.reasonForSaleData,
                bankInformationData: context.bankInformationData,
                investmentApplicationId: context?.investmentApplicationId,
                isJA: context?.isJA,
            }));
        }
    }, [context, t]);

    if (!context) {
        throw new Error('useCreateCaseManager must be used within a CreateCaseManagerProvider');
    }

    return context;
};
